import '@assets/styles/pages/site-visit-recording-fab.scss';
import { ReactElement, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';
import { SiteVisitRecordingControlsPanel } from './SiteVisitRecordingControlsPanel';
import { useSiteVisitRecordingSessionState } from './useSiteVisitRecordingSessionState';

interface DraggableReturn {
	ref: React.RefObject<HTMLElement>;
	style: React.CSSProperties;
	handlers: {
		onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
		onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
		onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
		onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
	};
}

function useDraggable(): DraggableReturn {
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const isDragging = useRef(false);
	const dragStart = useRef({ x: 0, y: 0 });
	const initialPos = useRef({ x: 0, y: 0 });
	const ref = useRef<HTMLElement>(null);

	const handlePointerDown = (e: React.PointerEvent<HTMLElement>): void => {
		if (e.button !== 0) return; // Only left click
		// Don't drag if clicking a button
		if ((e.target as HTMLElement).closest('button')) return;

		isDragging.current = true;
		dragStart.current = { x: e.clientX, y: e.clientY };
		initialPos.current = { ...position };

		e.currentTarget.setPointerCapture(e.pointerId);
	};

	const handlePointerMove = (e: React.PointerEvent<HTMLElement>): void => {
		if (!isDragging.current || !ref.current) return;

		const deltaX = e.clientX - dragStart.current.x;
		const deltaY = e.clientY - dragStart.current.y;

		let newX = initialPos.current.x + deltaX;
		let newY = initialPos.current.y + deltaY;

		// Bounding logic to keep it on screen
		const rect = ref.current.getBoundingClientRect();
		const baseX = rect.left - position.x;
		const baseY = rect.top - position.y;

		const proposedLeft = baseX + newX;
		const proposedRight = proposedLeft + rect.width;
		const proposedTop = baseY + newY;
		const proposedBottom = proposedTop + rect.height;

		// Clamp X
		if (proposedLeft < 0) {
			newX = -baseX;
		} else if (proposedRight > window.innerWidth) {
			newX = window.innerWidth - rect.width - baseX;
		}

		// Clamp Y
		if (proposedTop < 0) {
			newY = -baseY;
		} else if (proposedBottom > window.innerHeight) {
			newY = window.innerHeight - rect.height - baseY;
		}

		setPosition({ x: newX, y: newY });
	};

	const handlePointerUp = (e: React.PointerEvent<HTMLElement>): void => {
		if (!isDragging.current) return;
		isDragging.current = false;
		e.currentTarget.releasePointerCapture(e.pointerId);
	};

	return {
		ref,
		style: {
			transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
			cursor: isDragging.current ? 'grabbing' : 'grab',
			touchAction: 'none', // Prevent scrolling while dragging on touch devices
			userSelect: 'none' as const, // Prevent text selection while dragging
		},
		handlers: {
			onPointerDown: handlePointerDown,
			onPointerMove: handlePointerMove,
			onPointerUp: handlePointerUp,
			onPointerCancel: handlePointerUp,
		},
	};
}

/** Top-right recording bar for the site visit form flow (not post–visit feedback). */
export function SiteVisitRecordingFloatingControls(): ReactElement | null {
	const { isActive } = useSiteVisitRecordingSessionState();
	const uploadOnStop = useSiteVisitRecordingStore((s) => s.uploadOnStop);
	const { ref, style, handlers } = useDraggable();

	if (!isActive || uploadOnStop) {
		return null;
	}

	const fab = (
		<section
			ref={ref}
			style={style}
			{...handlers}
			className="site-visit-recording-fab"
			aria-label="Site visit recording controls"
			aria-live="polite"
		>
			<SiteVisitRecordingControlsPanel className="site-visit-recording-fab-panel" />
		</section>
	);

	return createPortal(fab, document.body);
}
